import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import type { PromotionPolicyImportProductMatch } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { serverMessage } from '~/lib/api/server-error';
import { cn } from '~/lib/utils';
import { usePromotionProductImport } from '~/hooks/use-promotion-product-import';

const ALLOWED_EXTENSIONS = ['.xlsx', '.csv', '.txt'];
const ACCEPT = ALLOWED_EXTENSIONS.join(',');

const hasAllowedExtension = (name: string) => ALLOWED_EXTENSIONS.some(ext => name.toLowerCase().endsWith(ext));

interface BulkImportProductsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Called with the products that resolved, so the parent can merge them into `productIds`. */
	onImported: (matched: PromotionPolicyImportProductMatch[]) => void;
}

/**
 * Paste and/or upload a list of product codes/barcodes to bulk-add products to the promotion policy.
 * Resolution happens server-side (PromotionPolicies/ImportProducts); matched ids are merged into the
 * editor's product selection via `onImported` and persisted on the normal Save. Unmatched codes are
 * surfaced so the user can correct them (dialog stays open while any remain).
 */
export function BulkImportProductsDialog({ open, onOpenChange, onImported }: BulkImportProductsDialogProps) {
	const { t } = useTranslation();
	const bi = (k: string, opts?: Record<string, unknown>) => t(`promotion.conditions.productSelection.bulkImport.${k}`, opts);

	const [codes, setCodes] = useState('');
	const [file, setFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [unmatched, setUnmatched] = useState<string[] | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const importProducts = usePromotionProductImport();

	const clearFileInput = () => {
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const reset = () => {
		setCodes('');
		setFile(null);
		setUnmatched(null);
		setDragActive(false);
		clearFileInput();
	};

	// Validate the picked/dropped file's extension client-side (the file <input> only guards the picker,
	// not drag&drop) so an unsupported file gives immediate feedback instead of a generic server error.
	const selectFile = (picked: File | null) => {
		if (!picked) return;
		if (!hasAllowedExtension(picked.name)) {
			toast.error(bi('invalidFile'));
			return;
		}
		setFile(picked);
	};

	const onPickFile = (e: ChangeEvent<HTMLInputElement>) => selectFile(e.target.files?.[0] ?? null);

	const onDrop = (e: DragEvent) => {
		e.preventDefault();
		setDragActive(false);
		selectFile(e.dataTransfer.files?.[0] ?? null);
	};

	const canSubmit = (codes.trim().length > 0 || file != null) && !importProducts.isPending;

	const runImport = () => {
		if (!canSubmit) return;
		importProducts.mutate(
			{ codes: codes.trim() || undefined, file: file ?? undefined },
			{
				onSuccess: result => {
					const matched = result.matchedProducts ?? [];
					onImported(matched);
					const unmatchedCodes = result.unmatchedCodes ?? [];
					setUnmatched(unmatchedCodes);
					toast.success(bi('summary', { matched: matched.length, total: result.totalSubmitted ?? 0 }));
					// Clear inputs but keep the unmatched report visible; close only when everything matched.
					setCodes('');
					setFile(null);
					clearFileInput();
					if (unmatchedCodes.length === 0) onOpenChange(false);
				},
				onError: (err: unknown) => toast.error(serverMessage(err))
			}
		);
	};

	const handleOpenChange = (next: boolean) => {
		if (!next) reset();
		onOpenChange(next);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{bi('title')}</DialogTitle>
					<DialogDescription>{bi('subtitle')}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div>
						<label className="mb-1.5 block text-sm font-medium text-foreground">{bi('pasteLabel')}</label>
						<Textarea rows={5} value={codes} onChange={e => setCodes(e.target.value)} placeholder={bi('pastePlaceholder')} />
						<p className="mt-1.5 text-xs text-foreground-muted">{bi('pasteHelp')}</p>
					</div>

					<div>
						<label className="mb-1.5 block text-sm font-medium text-foreground">{bi('fileLabel')}</label>
						<div
							onDragOver={e => {
								e.preventDefault();
								if (!dragActive) setDragActive(true);
							}}
							onDragLeave={e => {
								e.preventDefault();
								// Ignore drag-leave bubbling from child elements (icon/button) to avoid flicker.
								if (e.currentTarget === e.target) setDragActive(false);
							}}
							onDrop={onDrop}
							className={cn(
								'flex flex-col items-center gap-2 rounded-md border border-dashed border-border p-4 text-center transition-colors',
								dragActive && 'border-brand bg-accent/50'
							)}>
							<FileSpreadsheet size={20} className="text-foreground-muted" />
							{file ? (
								<div className="flex items-center gap-2 text-sm text-foreground">
									<span className="truncate">{file.name}</span>
									<button
										type="button"
										onClick={() => {
											setFile(null);
											clearFileInput();
										}}
										className="text-foreground-muted hover:text-foreground">
										<X size={14} />
									</button>
								</div>
							) : (
								<p className="text-xs text-foreground-muted">{bi('fileDropHint')}</p>
							)}
							<Button variant="outline" size="sm" type="button" onClick={() => fileInputRef.current?.click()}>
								{bi('chooseFile')}
							</Button>
							<input ref={fileInputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPickFile} />
						</div>
						<p className="mt-1.5 text-xs text-foreground-subtle">{bi('allowedExtensions')}</p>
					</div>

					{unmatched != null &&
						(unmatched.length === 0 ? (
							<div className="flex items-center gap-2 rounded-md border border-success-border bg-success-subtle p-3 text-sm text-success-fg">
								<CheckCircle2 size={16} className="shrink-0" /> {bi('allMatched')}
							</div>
						) : (
							<div className="rounded-md border border-warning-border bg-warning-subtle p-3">
								<p className="text-sm font-medium text-warning-fg">{bi('unmatchedTitle', { count: unmatched.length })}</p>
								<ul className="mt-1.5 max-h-32 list-disc overflow-y-auto pl-5 text-sm text-warning-fg">
									{unmatched.map((code, i) => (
										<li key={`${code}-${i}`} className="font-mono">
											{code}
										</li>
									))}
								</ul>
							</div>
						))}
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={() => handleOpenChange(false)}>
						{t('common.close')}
					</Button>
					<Button disabled={!canSubmit} onClick={runImport}>
						{importProducts.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
						{bi('importCta')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
